import { Merchant, User, Activation } from "src/models";
import { action, computed, observable } from "mobx";
import { createContext, useContext } from "react";
import { AsyncStore } from "src/stores";
import { IMerchantJSON, IActivationJSON, IKtpScanResult } from "src/interfaces";
import { PassportService } from "src/services";
import { asyncRequest } from "src/decorators";
import { CookieConfig } from "src/helpers";
import { LivenessLocale } from "src/constants";

const KTP_SCAN_DOCUMENT = "ktp";

export type IPassportPaymentMethod = "card" | "basic";

export type IPassportBusinessType = "individual" | "corporate";

export class PassportStore extends AsyncStore<PassportStore> {
  @observable
  authUser = new User();

  @observable
  outlet = new Merchant();

  @observable
  activation = new Activation();

  @observable
  isAuthenticated = false;

  service = new PassportService();

  @observable
  selectedPaymentMethod: IPassportPaymentMethod = "basic";

  @observable
  selectedBusinessType: IPassportBusinessType = "individual";

  @observable
  fetchUrl!: string;

  @computed
  get isCorporate(): boolean {
    return this.selectedBusinessType === "corporate";
  }

  @computed
  get isCardPayment(): boolean {
    return this.selectedPaymentMethod === "card";
  }

  @computed
  get isImprovementEnabled(): boolean {
    return this.outlet.featureTypes.includes("MIDTRANS_PASSPORT_ACTIVATION");
  }

  @action
  setToken(token: string) {
    this.service.tokenValue = token;
  }

  @action
  @asyncRequest("getCurrentUser")
  async getCurrentUser() {
    const response = await this.service.getCurrentUser();
    this.authUser = User.fromJSON(response.data?.data);
    this.isAuthenticated = true;
    CookieConfig.userId = this.authUser.id;
  }

  @action
  applySelectionFromData() {
    this.selectedPaymentMethod = this.outlet.metadata
      ?.midtrans_credit_card_optin
      ? "card"
      : "basic";
    this.selectedBusinessType = this.outlet
      .businessType as IPassportBusinessType;
  }

  @action
  @asyncRequest("getUserOutlet")
  async getUserOutlet() {
    const response = await this.service.getUserOutlet();
    this.outlet = Merchant.fromJSON(response.data?.data);
    this.applySelectionFromData();
  }

  @action
  @asyncRequest("updateOutlet")
  async updateOutlet(data: Partial<IMerchantJSON>, ignoreJSON = false) {
    const response = await this.service.updateOutlet(data);
    this.outlet = Merchant.fromJSON(response.data?.data, ignoreJSON);
    this.applySelectionFromData();
  }

  @action
  @asyncRequest("addFeatureTypes")
  async addFeatureTypes(features: string[]) {
    await this.service.addFeatureTypes(features);
    await this.getUserOutlet();
  }

  @action
  @asyncRequest("getActivation")
  async getActivation() {
    const response = await this.service.getPassportActivation();
    this.activation = Activation.fromJSON(response.data.data);
  }

  @action
  @asyncRequest("updateActivation")
  async updateActivation(data: Partial<IActivationJSON>) {
    await this.service.updatePassportActivation(data);
    this.activation.updatePartial(data);
  }

  async startKtpScan(fileType: string): Promise<IKtpScanResult> {
    const { data } = await this.service.startKtpScan({
      document_type: KTP_SCAN_DOCUMENT,
      file_type: fileType,
    });
    return data.data;
  }

  // undefined when the merchant has no scan yet (404)
  async getKtpScanResult(): Promise<IKtpScanResult | undefined> {
    try {
      const { data } = await this.service.getKtpScanResult(KTP_SCAN_DOCUMENT);
      return data.data;
    } catch (error) {
      if ((error as any)?.response?.status === 404) return undefined;
      throw error;
    }
  }

  @action
  @asyncRequest("updateOutletAndActivation")
  async updateOutletAndActivation(
    outletData: Partial<IMerchantJSON>,
    activationData: Partial<IActivationJSON>
  ) {
    const outletResponse = await this.service.updateOutlet(outletData);
    await this.service.updatePassportActivation(activationData);

    this.outlet = Merchant.fromJSON(outletResponse.data?.data, false);
    this.activation.updatePartial(activationData);
    this.applySelectionFromData();
  }

  @asyncRequest("generateUrl")
  async generateUrl(userId: number, key: string) {
    const { data } = await this.service.generateUrl(userId, key);
    return data;
  }

  @action
  @asyncRequest("uploadFile")
  async uploadFile(userId: number, file: File) {
    const { upload_url, fetch_url } = await this.generateUrl(userId, file.name);
    await this.service.uploadFile(upload_url, file);
    this.fetchUrl = fetch_url;
    return fetch_url;
  }

  @asyncRequest("generateUrlOss")
  async generateUrlOss(documentType: string, fileType: string) {
    const { data } = await this.service.generateUrlOss(documentType, fileType);
    return data;
  }

  @action
  @asyncRequest("uploadFileOss")
  async uploadFileOss(file: File, documentType: string) {
    const { data } = await this.generateUrlOss(documentType, file.type);
    const { upload_url, download_url } = data;

    await this.service.uploadFileOss(upload_url, file);
    this.fetchUrl = download_url;
    return download_url;
  }

  @action
  @asyncRequest("doPassportKYB")
  async doPassportKYB(isWni: boolean) {
    const data = {
      merchant_id: this.outlet.id,
      nik: this.outlet.idNumber,
      verify_basic: true,
      verify_selfie: true,
      name: this.outlet.idName,
      is_wna: !isWni,
    };

    await this.service.doPassportKYB(data);
    return;
  }

  @action
  @asyncRequest("recordConsent")
  async recordConsent(versionCode: string) {
    return await this.service.postConsent(versionCode);
  }

  @action
  @asyncRequest("createLivenessUrl")
  async createLivenessUrl(
    useQR: boolean,
    isMobile: boolean,
    locale: LivenessLocale
  ) {
    const response = await this.service.createLivenessLaunchUrl(
      useQR,
      isMobile,
      locale
    );
    return response.data?.data?.launch_url;
  }

  @action
  @asyncRequest("verifyLiveness")
  async verifyLiveness(sessionId: string) {
    const response = await this.service.verifyLiveness(sessionId);
    return response.data?.data;
  }
}

export const PassportStoreContext = createContext(new PassportStore());

export const usePassportStore = () => useContext(PassportStoreContext);
